import React, { useState, useEffect } from "react";
import T from '../../../components/common/T';
import { XMarkIcon, PlusIcon } from "@heroicons/react/24/outline";
// FIX: corrected path — this file lives in src/pages/Projects/components/ so needs ../../../
import { useCustomFields } from '../../../hooks/useCustomFields';
import CustomFieldRenderer from '../../../components/common/CustomFieldRenderer';

const ProjectEditor = ({ initialData, onSave, onCancel, readOnly = false }) => {
  const [formData, setFormData] = useState({
    name: "",
    client: "",
    startDate: "",
    endDate: "",
    budget: "",
    status: "Draft",
    description: "",
    projectManager: "",
    tags: []
  });

  const [tagInput, setTagInput] = useState("");

  // FIX: use initialData?.id — ProjectEditor receives the project via props, not useParams
  const { customData, setCustomData, saveCustomFields } = useCustomFields('projects', initialData?.id ?? null);

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        tags: initialData.tags || []
      });
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddTag = (e) => {
    if (e.key === 'Enter' || e.type === 'click') {
      e.preventDefault();
      if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
        setFormData(prev => ({
          ...prev,
          tags: [...prev.tags, tagInput.trim()]
        }));
        setTagInput("");
      }
    }
  };

  const removeTag = (tagToRemove) => {
    if (readOnly) return;
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // For edit mode, save custom fields with the existing project ID
    // For create mode, the parent must call saveCustomFields(newId) after getting the new ID
    if (initialData?.id) {
      await saveCustomFields(initialData.id);
    }
    onSave(formData);
  };

  return (
    <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] rounded-xl shadow-xs p-4 md:p-6 transition-colors">
      <div className="flex justify-between items-center mb-6 border-b border-gray-100 dark:border-[#30363d] pb-4">
        <h2 className="text-base font-bold text-gray-800 dark:text-[#f0f6fc]">
          {initialData?.id ? "Edit Project Details" : "Create New Project"}
        </h2>
        {readOnly && <span className="bg-red-100 dark:bg-red-950/50 text-red-700 dark:text-red-400 text-xs font-bold px-2.5 py-1 rounded border border-red-200 dark:border-red-800/50"><T>Read Only Mode</T></span>}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Row 1 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-xs font-bold text-gray-500 dark:text-[#848d97] uppercase mb-1.5"><T>Project Name</T></label>
            <input
              type="text"
              name="name"
              disabled={readOnly}
              required
              className="w-full border border-gray-300 dark:border-[#30363d] bg-white dark:bg-[#21262d] text-gray-900 dark:text-white p-2.5 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none disabled:bg-gray-100 dark:disabled:bg-[#161b22]"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g. Skyline Tower Renovation"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 dark:text-[#848d97] uppercase mb-1.5"><T>Client Name</T></label>
            <input
              type="text"
              name="client"
              disabled={readOnly}
              required
              className="w-full border border-gray-300 dark:border-[#30363d] bg-white dark:bg-[#21262d] text-gray-900 dark:text-white p-2.5 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none disabled:bg-gray-100 dark:disabled:bg-[#161b22]"
              value={formData.client}
              onChange={handleChange}
              placeholder="e.g. Acme Corp"
            />
          </div>
        </div>

        {/* Row 2 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-xs font-bold text-gray-500 dark:text-[#848d97] uppercase mb-1.5"><T>Start Date</T></label>
            <input
              type="date"
              name="startDate"
              disabled={readOnly}
              className="w-full border border-gray-300 dark:border-[#30363d] bg-white dark:bg-[#21262d] text-gray-900 dark:text-white p-2.5 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none disabled:bg-gray-100 dark:disabled:bg-[#161b22]"
              value={formData.startDate}
              onChange={handleChange}
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 dark:text-[#848d97] uppercase mb-1.5"><T>End Date</T></label>
            <input
              type="date"
              name="endDate"
              disabled={readOnly}
              className="w-full border border-gray-300 dark:border-[#30363d] bg-white dark:bg-[#21262d] text-gray-900 dark:text-white p-2.5 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none disabled:bg-gray-100 dark:disabled:bg-[#161b22]"
              value={formData.endDate}
              onChange={handleChange}
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 dark:text-[#848d97] uppercase mb-1.5"><T>Total Budget</T></label>
            <div className="relative">
              <span className="absolute left-3 top-2.5 text-gray-500 dark:text-[#848d97] font-bold">₹</span>
              <input
                type="number"
                name="budget"
                disabled={readOnly}
                className="w-full border border-gray-300 dark:border-[#30363d] bg-white dark:bg-[#21262d] text-gray-900 dark:text-white p-2.5 pl-7 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none disabled:bg-gray-100 dark:disabled:bg-[#161b22]"
                value={formData.budget}
                onChange={handleChange}
                placeholder="0.00"
              />
            </div>
          </div>
        </div>

        {/* Row 3: TAGS & MANAGER */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-xs font-bold text-gray-500 dark:text-[#848d97] uppercase mb-1.5"><T>Project Tags</T></label>
            <div className={`w-full border border-gray-300 dark:border-[#30363d] p-2 rounded-lg text-sm bg-white dark:bg-[#21262d] ${readOnly ? "bg-gray-100 dark:bg-[#161b22]" : ""}`}>
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.tags.map((tag, index) => (
                  <span key={index} className="bg-orange-50 dark:bg-orange-950/40 text-[#D9531E] dark:text-[#F5875E] border border-orange-200/80 dark:border-orange-800/50 text-xs font-bold px-2.5 py-1 rounded-md flex items-center gap-1.5">
                    {tag}
                    {!readOnly && (
                      <button type="button" onClick={() => removeTag(tag)} className="hover:text-red-500 transition">
                        <XMarkIcon className="w-3 h-3" />
                      </button>
                    )}
                  </span>
                ))}
              </div>
              {!readOnly && (
                <div className="flex items-center gap-2">
                  <input 
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleAddTag}
                    placeholder="Type tag & hit Enter..."
                    className="flex-1 outline-none text-xs text-gray-700 dark:text-white bg-transparent"
                  />
                  <button type="button" onClick={handleAddTag} className="text-[#D9531E] hover:text-[#C24514] transition p-1">
                    <PlusIcon className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 dark:text-[#848d97] uppercase mb-1.5"><T>Project Manager</T></label>
            <input
              type="text"
              name="projectManager"
              disabled={readOnly}
              className="w-full border border-gray-300 dark:border-[#30363d] bg-white dark:bg-[#21262d] text-gray-900 dark:text-white p-2.5 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none disabled:bg-gray-100 dark:disabled:bg-[#161b22]"
              value={formData.projectManager}
              onChange={handleChange}
              placeholder="Assign a Manager"
            />
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-xs font-bold text-gray-500 dark:text-[#848d97] uppercase mb-1.5"><T>Description</T></label>
          <textarea
            name="description"
            disabled={readOnly}
            rows={4}
            className="w-full border border-gray-300 dark:border-[#30363d] bg-white dark:bg-[#21262d] text-gray-900 dark:text-white p-2.5 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none disabled:bg-gray-100 dark:disabled:bg-[#161b22]"
            value={formData.description}
            onChange={handleChange}
            placeholder="Describe the project scope..."
          />
        </div>

        {/* Actions */}
        {!readOnly && (
          <div className="space-y-4 pt-4 border-t border-gray-100 dark:border-[#30363d]">
            {/* Custom Fields rendered above the action buttons */}
            <CustomFieldRenderer module="projects" customData={customData} onChange={setCustomData} />

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onCancel}
                className="px-5 py-2.5 bg-white dark:bg-[#21262d] border border-gray-300 dark:border-[#30363d] text-gray-700 dark:text-[#848d97] text-sm font-bold rounded-lg hover:bg-gray-50 dark:hover:bg-[#30363d] transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2.5 bg-[#D9531E] hover:bg-[#C24514] active:scale-[0.98] text-white text-sm font-bold rounded-lg shadow-sm transition"
              >
                Save Project
              </button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default ProjectEditor;